import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reportError } from '../utils/errorReporting';

// Reflects the open popup (modal, tray, drawer) in the URL (?modal=<name>) so
// third-party analytics tools that key off URL/history changes can tell one
// popup flow from another. One-way (state → URL) and always a `replace` —
// nothing reads this param back to control any popup's `isOpen`, so a `push`
// would let browser Back change the URL without actually closing anything.
//
// Popups nest: a popup routinely opens a second popup while staying open
// underneath it (GetEarlierModal → `get-earlier-confirm`, the box tray →
// `product-detail`, …). There is only one `?modal=` param, so the open popups
// are kept in a shared stack here and the param always shows the top of it —
// the popup the customer is actually looking at. Letting each instance decide
// on its own made every nested pair fight: the child set `?modal=child`, the
// parent (still open, and re-running because `searchParams` was one of its
// effect deps) set it straight back to `?modal=parent`, and the two
// replaceState calls ping-ponged forever — a render loop that pegged the page
// and tripped Chrome's "100 replaceState calls per 10s" throttle.
const openPopups = [];

// Whether the `?modal=` value currently in the URL is one we put there. A
// value that arrived some other way (e.g. on a deep link) is never cleared.
let ownsParam = false;

let nextPopupId = 0;

// Always read the live URL rather than the `searchParams` from a render
// closure: several popups can open/close in a single commit, and each write
// below lands in `window.location` synchronously, so a closed-over copy is
// already stale by the time the next effect in the same commit runs.
const readModalParam = () => {
  try {
    return new URLSearchParams(window.location.search).get('modal');
  } catch (error) {
    return null;
  }
};

// Chrome (and other browsers) throttle history.replaceState/pushState to 100
// calls per 10s and throw a DOMException past that — seen in production as a
// full-app crash from a popup-heavy page. This sync is a nice-to-have for
// third-party analytics only, so a throttle exception must never be allowed to
// reach the ErrorBoundary.
const writeModalParam = (setSearchParams, desired) => {
  // Build the next params from the live URL so a stale `searchParams` can't
  // drop another param that was set moments ago.
  const next = new URLSearchParams(window.location.search);
  if (desired === null) {
    next.delete('modal');
  } else {
    next.set('modal', desired);
  }
  try {
    setSearchParams(next, { replace: true });
    ownsParam = desired !== null;
  } catch (error) {
    reportError(error, { source: 'popup_url_sync' });
  }
};

// Point the param at the top of the stack (the most recently opened popup), or
// clear it once nothing of ours is open. Skips the write entirely when the URL
// already says the right thing — `setSearchParams` navigates (and so calls
// history.replaceState) even when the value is unchanged.
const syncModalParam = (setSearchParams) => {
  const desired = openPopups.length ? openPopups[openPopups.length - 1].name : null;
  if (desired === null && !ownsParam) return;
  if (readModalParam() === desired) {
    if (desired === null) ownsParam = false;
    return;
  }
  writeModalParam(setSearchParams, desired);
};

export default function usePopupUrlSync(name, isOpen) {
  const [, setSearchParams] = useSearchParams();

  // `setSearchParams`'s identity isn't stable across navigations (it's memoized
  // on the current searchParams, which change identity on every URL update).
  // Keep the latest in a ref so the effect below can depend on this popup's own
  // open state alone: with dozens of always-mounted popups on a page (one
  // ProductModal per product card in the box builder, say), re-running on every
  // URL change turned a single write into a storm of them.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Identifies this instance in the stack. Popups can share a `name` (the
  // Overview detail view's deep-link GetEarlierModal alongside
  // SubscriptionDetail's own), so entries are matched by identity, not name.
  const idRef = useRef(null);
  if (idRef.current === null) {
    nextPopupId += 1;
    idRef.current = nextPopupId;
  }

  useEffect(() => {
    if (!name || !isOpen) return undefined;
    const entry = { id: idRef.current, name };
    openPopups.push(entry);
    syncModalParam(setSearchParamsRef.current);
    // Runs both when `isOpen` flips back to false and when the popup unmounts
    // while still open — some callers conditionally mount the whole component
    // (`{subscription && <StockupProductModal isOpen … />}`) instead of
    // flipping `isOpen`, which would otherwise leave `?modal=` stuck.
    return () => {
      const index = openPopups.indexOf(entry);
      if (index !== -1) openPopups.splice(index, 1);
      syncModalParam(setSearchParamsRef.current);
    };
  }, [isOpen, name]);
}
